import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faHeadset,
  faUser,
  faRobot,
  faTicket,
  faTimes,
  faSpinner,
  faComment
} from '@fortawesome/free-solid-svg-icons';
import { supportAPI } from '../services/api';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import './SupportChat.css';

const SupportChat = ({ isOpen, onClose }) => {
  const { showCustomToast } = useNotifications();
  const { isAuthenticated } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [newTicketData, setNewTicketData] = useState({
    subject: '',
    message: '',
    category: 'general'
  });
  const messagesEndRef = useRef(null);

  const categories = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'payment', label: 'Payment Issue' },
    { value: 'order', label: 'Order Issue' },
    { value: 'account', label: 'Account Issue' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    // Only fetch tickets if user is authenticated and modal is open
    if (isAuthenticated && isOpen) {
      fetchTickets();
    }
  }, [isAuthenticated, isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTickets = async () => {
    // Don't fetch if user is not authenticated
    if (!isAuthenticated) {
      return;
    }

    try {
      setLoading(true);
      const response = await supportAPI.getSupportTickets();
      if (response && response.data) {
        setTickets(response.data);
      }
    } catch (error) {
      // Only log error, don't show toast for auth errors
      if (error.message !== 'Authorization token required') {
        showCustomToast({
          type: 'error',
          title: 'Error',
          message: 'Failed to load support tickets'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTicket = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const response = await supportAPI.getSupportTicket(ticket.id);
      if (response && response.data) {
        // Backend returns 'responses' array, not 'messages'
        const ticketResponses = response.data.responses || [];
        // Transform responses to match UI expectations
        const formattedMessages = ticketResponses.map(resp => ({
          id: resp.id,
          message: resp.message,
          sender: resp.is_from_user ? 'user' : 'support',
          created_at: resp.created_at
        }));
        setMessages(formattedMessages);
      }
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to load ticket messages'
      });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedTicket) return;

    try {
      const tempMessage = {
        id: Date.now(),
        message: newMessage,
        sender: 'user',
        created_at: new Date().toISOString()
      };

      setMessages([...messages, tempMessage]);
      setNewMessage('');

      await supportAPI.addSupportMessage(selectedTicket.id, newMessage);

      showCustomToast({
        type: 'success',
        title: 'Success',
        message: 'Message sent successfully'
      });
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to send message'
      });
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();

    if (!newTicketData.subject.trim() || !newTicketData.message.trim()) {
      showCustomToast({
        type: 'warning',
        title: 'Validation Error',
        message: 'Please fill in all fields'
      });
      return;
    }

    try {
      setLoading(true);
      // Add type field required by unified ticket system
      const ticketPayload = {
        type: 'support',
        subject: newTicketData.subject,
        message: newTicketData.message,
        category: newTicketData.category,
        priority: 'medium'
      };

      await supportAPI.createSupportTicket(ticketPayload);

      showCustomToast({
        type: 'success',
        title: 'Success',
        message: 'Support ticket created successfully'
      });

      setShowNewTicketModal(false);
      setNewTicketData({
        subject: '',
        message: '',
        category: 'general'
      });

      fetchTickets();
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to create ticket'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;

    try {
      await supportAPI.closeSupportTicket(ticketId);

      showCustomToast({
        type: 'success',
        title: 'Success',
        message: 'Ticket closed successfully'
      });

      setSelectedTicket(null);
      setMessages([]);
      fetchTickets();
    } catch (error) {
      showCustomToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to close ticket'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      open: '#FF6B6B',
      in_progress: '#4ECDC4',
      resolved: '#45B7D1',
      closed: '#96CEB4'
    };
    return colors[status] || '#9CA3AF';
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="support-modal-overlay" onClick={onClose}>
        <div className="support-modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="support-chat-header">
            <div className="support-header-content">
              <FontAwesomeIcon icon={faHeadset} className="support-header-icon" />
              <div>
                <h1>Customer Support</h1>
                <p>Get help from our support team</p>
              </div>
            </div>
            <div className="support-header-actions">
              <button
                onClick={() => setShowNewTicketModal(true)}
                className="support-new-ticket-btn"
              >
                <FontAwesomeIcon icon={faTicket} />
                New Ticket
              </button>
              <button onClick={onClose} className="support-close-modal-btn">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          <div className="support-chat-content">
          {/* Tickets Sidebar */}
          <div className="support-tickets-sidebar">
            <div className="support-sidebar-header">
              <h3>Your Tickets</h3>
              <span className="support-ticket-count">{tickets.length}</span>
            </div>

            <div className="support-tickets-list">
              {loading && tickets.length === 0 ? (
                <div className="support-loading">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <p>Loading tickets...</p>
                </div>
              ) : tickets.length === 0 ? (
                <div className="support-empty-state">
                  <FontAwesomeIcon icon={faTicket} />
                  <p>No tickets yet</p>
                  <small>Create your first support ticket</small>
                </div>
              ) : (
                tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className={`support-ticket-item ${selectedTicket?.id === ticket.id ? 'active' : ''}`}
                    onClick={() => handleSelectTicket(ticket)}
                  >
                    <div className="support-ticket-header">
                      <h4>{ticket.subject}</h4>
                      <span
                        className="support-ticket-status"
                        style={{ backgroundColor: getStatusColor(ticket.status) }}
                      >
                        {ticket.status}
                      </span>
                    </div>
                    <p className="support-ticket-preview">
                      {ticket.category && `${ticket.category} • `}
                      {formatDate(ticket.created_at)}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="support-chat-area">
            {selectedTicket ? (
              <>
                <div className="support-chat-header-bar">
                  <div>
                    <h3>{selectedTicket.subject}</h3>
                    <p>Ticket #{selectedTicket.id}</p>
                  </div>
                  {selectedTicket.status !== 'closed' && (
                    <button
                      onClick={() => handleCloseTicket(selectedTicket.id)}
                      className="support-close-ticket-btn"
                    >
                      Close Ticket
                    </button>
                  )}
                </div>

                <div className="support-messages-container">
                  {messages.length === 0 ? (
                    <div className="support-no-messages">
                      <FontAwesomeIcon icon={faHeadset} size="3x" />
                      <p>No messages yet</p>
                      <small>Start the conversation by sending a message</small>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`support-message ${msg.sender === 'user' ? 'user' : 'support'}`}
                      >
                        <div className="support-message-avatar">
                          <FontAwesomeIcon
                            icon={msg.sender === 'user' ? faUser : faRobot}
                          />
                        </div>
                        <div className="support-message-content">
                          <div className="support-message-header">
                            <span className="support-message-sender">
                              {msg.sender === 'user' ? 'You' : 'Support Agent'}
                            </span>
                            <span className="support-message-time">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                          <div className="support-message-text">
                            {msg.message}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {selectedTicket.status !== 'closed' && (
                  <form onSubmit={handleSendMessage} className="support-message-input-form">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="support-message-input"
                    />
                    <button type="submit" className="support-send-btn">
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                  </form>
                )}
              </>
            ) : (
              <div className="support-no-selection">
                <FontAwesomeIcon icon={faHeadset} size="4x" />
                <h3>Select a ticket to view conversation</h3>
                <p>Or create a new ticket to get started</p>
              </div>
            )}
          </div>
        </div>

        {/* New Ticket Modal */}
        {showNewTicketModal && (
          <div className="support-ticket-modal-overlay" onClick={() => setShowNewTicketModal(false)}>
            <div className="support-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="support-modal-header">
                <h2>Create New Ticket</h2>
                <button
                  onClick={() => setShowNewTicketModal(false)}
                  className="support-modal-close"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleCreateTicket} className="support-ticket-form">
                <div className="support-form-group">
                  <label>Subject</label>
                  <input
                    type="text"
                    value={newTicketData.subject}
                    onChange={(e) => setNewTicketData({
                      ...newTicketData,
                      subject: e.target.value
                    })}
                    placeholder="Brief description of your issue"
                    required
                  />
                </div>

                <div className="support-form-group">
                  <label>Category</label>
                  <select
                    value={newTicketData.category}
                    onChange={(e) => setNewTicketData({
                      ...newTicketData,
                      category: e.target.value
                    })}
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="support-form-group">
                  <label>Message</label>
                  <textarea
                    value={newTicketData.message}
                    onChange={(e) => setNewTicketData({
                      ...newTicketData,
                      message: e.target.value
                    })}
                    placeholder="Describe your issue in detail..."
                    rows="6"
                    required
                  />
                </div>

                <div className="support-modal-actions">
                  <button
                    type="button"
                    onClick={() => setShowNewTicketModal(false)}
                    className="support-btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="support-btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Ticket'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default SupportChat;
